// ─────────────────────────────────────────────────────────────────────────────
// conf-tracks.tsx — Konferentsiya (c05) corporate variant: TECH SUMMIT 2026
//
// Reference language: Stripe Sessions × Figma Config × GitHub Universe. Cream
// paper (#F5EFE6), monochrome ink (#1A1814), Inter font everywhere. This is a
// 3-day multi-track tech conference. The centerpiece is a Multi-Track Schedule
// — 3 days × 4 tracks (Engineering / Product / Design / Business) with 4-5
// sessions per track, each tagged with track-color. Horizontal day tabs,
// vertical time grid.
//
// Signature element (hero): the day numbers "01 / 02 / 03" stagger-fade in as
// a stepper at hero load, each with a subtle bracket scale-in. Reduced-motion
// reveals them instantly with no scaling.
//
// Sound: 3-4 triggers — agenda row click (tap), speaker card hover (tap),
// RSVP tap (tap). NO chime — this is B2B. All sounds are subtle UI accents.
//
// Owned-file rules: this variant only touches its own file. It composes the
// shared sections (CountdownSection, StorySection, MapSection, RsvpSection,
// ShareSection, GuestFooter) and adds two unique sections (TrackSchedule,
// SpeakersGrid). NO music section per spec.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import {
  GuestPhoto,
  CountdownSection,
  StorySection,
  MapSection,
  RsvpSection,
  ShareSection,
  GuestFooter,
} from "@/components/sections";
import { useSound } from "@/lib/sound";
import { useT } from "@/lib/i18n";
import type { VariantProps } from "./types";

// ─── prefers-reduced-motion ─────────────────────────────────────────────────
function usePrefersReducedMotion(): boolean {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduce(mq.matches);
    const handler = (e: MediaQueryListEvent) => setReduce(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return reduce;
}

// ─── Track palette ──────────────────────────────────────────────────────────
// Even though the global accent is monochrome ink, individual tracks need
// distinguishable color tags or the schedule devolves into a wall of text.
// We pick four desaturated, paper-friendly hues that stand against cream.
type TrackKey = "eng" | "product" | "design" | "biz";

interface TrackMeta {
  key: TrackKey;
  label: string;
  color: string; // small accent dot/line
}

const TRACKS: TrackMeta[] = [
  { key: "eng", label: "Engineering", color: "#2F6FED" },
  { key: "product", label: "Product", color: "#C44B2A" },
  { key: "design", label: "Design", color: "#7A4FB5" },
  { key: "biz", label: "Business", color: "#1F7A5A" },
];

// ─── Schedule data ──────────────────────────────────────────────────────────
// Three days × four tracks × 4-5 sessions. Time-aligned rows so the grid feels
// like a real conference matrix. Times are 24h, displayed `HH:MM`.
interface Session {
  time: string;
  title: string;
  speaker: string;
  track: TrackKey;
}

interface DayPlan {
  num: string;
  date: string;
  title: string;
  sessions: Session[];
}

const SCHEDULE: DayPlan[] = [
  {
    num: "01",
    date: "10 OCT",
    title: "Foundations",
    sessions: [
      // Engineering
      { time: "09:00", title: "Opening Keynote — The Decade of Platforms", speaker: "Sardor Akhmedov", track: "eng" },
      { time: "10:30", title: "Scaling Postgres past 10TB", speaker: "Madina Rakhimova", track: "eng" },
      { time: "13:00", title: "Edge runtimes vs traditional servers", speaker: "Bekzod Yusupov", track: "eng" },
      { time: "15:00", title: "Observability that earns its budget", speaker: "Nodira Karimova", track: "eng" },
      // Product
      { time: "09:30", title: "Mahsulot strategiyasi: Asia bozori", speaker: "Dilnoza Toshpulatova", track: "product" },
      { time: "11:00", title: "From 0 → 1: shipping with constraints", speaker: "Akmal Khojaev", track: "product" },
      { time: "13:30", title: "Pricing experiments that don't burn trust", speaker: "Zukhra Pulatova", track: "product" },
      { time: "15:30", title: "Roadmaps as conversations", speaker: "Sardor Akhmedov", track: "product" },
      // Design
      { time: "10:00", title: "Tipografika: Inter, IBM Plex va Cyrillic", speaker: "Aziza Yusupova", track: "design" },
      { time: "11:30", title: "Design systems for 12-person teams", speaker: "Jasur Kim", track: "design" },
      { time: "14:00", title: "Motion as information, not decoration", speaker: "Madina Rakhimova", track: "design" },
      { time: "16:00", title: "Accessibility audit live walkthrough", speaker: "Aziza Yusupova", track: "design" },
      // Business
      { time: "10:00", title: "Building a SaaS in CIS — go-to-market", speaker: "Akmal Khojaev", track: "biz" },
      { time: "12:00", title: "Hiring senior engineers in Tashkent", speaker: "Zukhra Pulatova", track: "biz" },
      { time: "14:30", title: "Investor narratives for B2B founders", speaker: "Bekzod Yusupov", track: "biz" },
      { time: "16:30", title: "Local talent, global product", speaker: "Dilnoza Toshpulatova", track: "biz" },
    ],
  },
  {
    num: "02",
    date: "11 OCT",
    title: "Build Day",
    sessions: [
      { time: "09:00", title: "Type-safe APIs at scale (TS + Go)", speaker: "Sardor Akhmedov", track: "eng" },
      { time: "10:30", title: "Incident response: postmortem live", speaker: "Madina Rakhimova", track: "eng" },
      { time: "13:00", title: "From monolith to modular monolith", speaker: "Bekzod Yusupov", track: "eng" },
      { time: "15:00", title: "Workshop: testing that pays off", speaker: "Nodira Karimova", track: "eng" },

      { time: "09:30", title: "Discovery sprintlari amaliyotda", speaker: "Dilnoza Toshpulatova", track: "product" },
      { time: "11:00", title: "Activation metrics nobody talks about", speaker: "Akmal Khojaev", track: "product" },
      { time: "13:30", title: "When to kill a feature gracefully", speaker: "Zukhra Pulatova", track: "product" },
      { time: "16:00", title: "Pricing pages that convert in CIS", speaker: "Sardor Akhmedov", track: "product" },

      { time: "10:00", title: "Prototyping in Figma → production", speaker: "Aziza Yusupova", track: "design" },
      { time: "11:30", title: "Light/dark mode without compromise", speaker: "Jasur Kim", track: "design" },
      { time: "14:00", title: "Workshop: rebuilding a checkout", speaker: "Madina Rakhimova", track: "design" },
      { time: "15:30", title: "Brand × product — finding the seam", speaker: "Aziza Yusupova", track: "design" },

      { time: "10:00", title: "B2B sales: first 10 customers", speaker: "Akmal Khojaev", track: "biz" },
      { time: "12:00", title: "Compliance lite: SOC 2 in Asia", speaker: "Zukhra Pulatova", track: "biz" },
      { time: "14:30", title: "From freelancer to founder", speaker: "Bekzod Yusupov", track: "biz" },
      { time: "16:30", title: "Operating cadence for 2 — 50 people", speaker: "Dilnoza Toshpulatova", track: "biz" },
    ],
  },
  {
    num: "03",
    date: "12 OCT",
    title: "Forward",
    sessions: [
      { time: "09:00", title: "AI in production: cost & latency reality", speaker: "Sardor Akhmedov", track: "eng" },
      { time: "10:30", title: "WebAssembly outside the browser", speaker: "Madina Rakhimova", track: "eng" },
      { time: "13:00", title: "Closing keynote — the next 10 years", speaker: "Bekzod Yusupov", track: "eng" },
      { time: "15:00", title: "Open Q&A — Engineering panel", speaker: "Nodira Karimova", track: "eng" },

      { time: "09:30", title: "AI o'rnatilgan mahsulotlar", speaker: "Dilnoza Toshpulatova", track: "product" },
      { time: "11:00", title: "Product-led growth in B2B SaaS", speaker: "Akmal Khojaev", track: "product" },
      { time: "13:30", title: "Saying no — a product principle", speaker: "Zukhra Pulatova", track: "product" },
      { time: "15:30", title: "Closing — Product panel", speaker: "Sardor Akhmedov", track: "product" },

      { time: "10:00", title: "Designing for AI surfaces", speaker: "Aziza Yusupova", track: "design" },
      { time: "11:30", title: "Performance is a design problem", speaker: "Jasur Kim", track: "design" },
      { time: "14:00", title: "Critique culture done right", speaker: "Madina Rakhimova", track: "design" },
      { time: "16:00", title: "Closing — Design panel", speaker: "Aziza Yusupova", track: "design" },

      { time: "10:00", title: "Fundraising — series A in 2026", speaker: "Akmal Khojaev", track: "biz" },
      { time: "12:00", title: "Building remote-first from Tashkent", speaker: "Zukhra Pulatova", track: "biz" },
      { time: "14:30", title: "Exits, M&A — the CIS playbook", speaker: "Bekzod Yusupov", track: "biz" },
      { time: "16:30", title: "Closing keynote — Yangi avlod", speaker: "Dilnoza Toshpulatova", track: "biz" },
    ],
  },
];

// ─── Speaker data ───────────────────────────────────────────────────────────
interface Speaker {
  name: string;
  role: string;
  track: TrackKey;
  photo: string;
}

const SPEAKERS: Speaker[] = [
  {
    name: "Sardor Akhmedov",
    role: "Principal Engineer · Bonjur",
    track: "eng",
    photo: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=600&q=80&auto=format&fit=crop",
  },
  {
    name: "Dilnoza Toshpulatova",
    role: "Head of Product · Click",
    track: "product",
    photo: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=600&q=80&auto=format&fit=crop",
  },
  {
    name: "Aziza Yusupova",
    role: "Design Director · UZUM",
    track: "design",
    photo: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=600&q=80&auto=format&fit=crop",
  },
  {
    name: "Akmal Khojaev",
    role: "Founder & CEO · Stafera",
    track: "biz",
    photo: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=600&q=80&auto=format&fit=crop",
  },
  {
    name: "Madina Rakhimova",
    role: "Staff Engineer · Payme",
    track: "eng",
    photo: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=600&q=80&auto=format&fit=crop",
  },
  {
    name: "Bekzod Yusupov",
    role: "VP Engineering · Humans",
    track: "eng",
    photo: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=600&q=80&auto=format&fit=crop",
  },
];

// ─── Day stepper (signature element) ────────────────────────────────────────
// "01 / 02 / 03" stagger-fade in at hero load. Each number is enclosed in
// brackets [01] that scale from 0.6 → 1.0 in sync with the fade. Reduced
// motion: render full opacity, no scale.
function DayStepper({
  reduce,
  ink,
  font,
}: {
  reduce: boolean;
  ink: string;
  font: string;
}) {
  // visible[i] flips true after a small delay so each day pops in turn.
  const [visible, setVisible] = useState<[boolean, boolean, boolean]>([
    reduce, reduce, reduce,
  ]);

  useEffect(() => {
    if (reduce) {
      setVisible([true, true, true]);
      return;
    }
    const t1 = window.setTimeout(() => setVisible([true, false, false]), 150);
    const t2 = window.setTimeout(() => setVisible([true, true, false]), 380);
    const t3 = window.setTimeout(() => setVisible([true, true, true]), 610);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);
    };
  }, [reduce]);

  const labels = ["01", "02", "03"];
  return (
    <div
      aria-label="Conference days"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 18,
        fontFamily: font,
        fontFeatureSettings: '"tnum" 1, "lnum" 1',
        flexWrap: "wrap",
      }}
    >
      {labels.map((l, i) => {
        const on = visible[i];
        return (
          <div key={l} style={{ display: "flex", alignItems: "center", gap: 18 }}>
            <span
              style={{
                display: "inline-flex",
                alignItems: "baseline",
                gap: 4,
                opacity: on ? 1 : 0,
                transform: on ? "scale(1)" : "scale(0.6)",
                transformOrigin: "center",
                transition: reduce
                  ? "none"
                  : "opacity 280ms ease, transform 320ms cubic-bezier(.2,.7,.2,1)",
                color: ink,
              }}
            >
              <span style={{ fontWeight: 400, fontSize: 22, opacity: 0.55 }}>[</span>
              <span style={{ fontWeight: 800, fontSize: 28, letterSpacing: "-0.02em" }}>{l}</span>
              <span style={{ fontWeight: 400, fontSize: 22, opacity: 0.55 }}>]</span>
            </span>
            {i < labels.length - 1 && (
              <span
                aria-hidden
                style={{
                  width: 28,
                  height: 1,
                  background: ink,
                  opacity: on ? 0.45 : 0,
                  transition: reduce ? "none" : "opacity 280ms ease",
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Track tag pill ─────────────────────────────────────────────────────────
function TrackTag({ track, font }: { track: TrackKey; font: string }) {
  const meta = TRACKS.find((t) => t.key === track) ?? TRACKS[0];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "3px 8px",
        border: `1px solid ${meta.color}55`,
        background: `${meta.color}10`,
        color: meta.color,
        fontFamily: font,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.18em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
      }}
    >
      <span
        aria-hidden
        style={{ width: 6, height: 6, background: meta.color, borderRadius: 1 }}
      />
      {meta.label}
    </span>
  );
}

// ─── Track schedule (centerpiece) ───────────────────────────────────────────
// Day tabs across the top (horizontal). Below: a vertical-by-time stack of
// rows; each row is split into 4 track columns. On mobile, columns collapse
// to a stacked accordion-feel single column with track tags inline.
function TrackSchedule({
  t,
  reduce,
  onSessionTap,
}: {
  t: VariantProps["t"];
  reduce: boolean;
  onSessionTap: () => void;
}) {
  const tt = useT();
  const [dayIdx, setDayIdx] = useState(0);
  const day = SCHEDULE[dayIdx];

  // Group sessions into rows by unique time slots, ordered ascending.
  const grid = useMemo(() => {
    const times = Array.from(new Set(day.sessions.map((s) => s.time))).sort();
    return times.map((time) => {
      const row: Record<TrackKey, Session | null> = {
        eng: null,
        product: null,
        design: null,
        biz: null,
      };
      for (const s of day.sessions) {
        if (s.time === time) row[s.track] = s;
      }
      return { time, row };
    });
  }, [day]);

  return (
    <section
      data-section="agenda"
      style={{
        padding: "120px clamp(20px, 5vw, 48px)",
        borderTop: `1px solid ${t.ink}22`,
      }}
    >
      <div style={{ maxWidth: 1240, margin: "0 auto" }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <div
            style={{
              fontFamily: t.font,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.4em",
              color: t.accent,
              marginBottom: 12,
            }}
          >
            {tt("variant.c05.agenda.eyebrow") || "MULTI-TRACK SCHEDULE"}
          </div>
          <h2
            style={{
              fontFamily: t.font,
              fontSize: "clamp(36px, 5vw, 56px)",
              fontWeight: 800,
              letterSpacing: "-0.02em",
              margin: 0,
            }}
          >
            3 kun · 4 ta yo'nalish
          </h2>
          <p
            style={{
              maxWidth: 540,
              margin: "16px auto 0",
              fontFamily: t.font,
              fontSize: 15,
              lineHeight: 1.6,
              color: `${t.ink}cc`,
            }}
          >
            Engineering, Product, Design and Business tracks running in parallel.
            Tap any row to see session details. Har bir kun — yangi mavzu.
          </p>
        </div>

        {/* Day tabs */}
        <div
          role="tablist"
          aria-label="Days"
          style={{
            display: "flex",
            gap: 8,
            marginBottom: 24,
            overflowX: "auto",
            paddingBottom: 6,
          }}
          className="conf-day-tabs"
        >
          {SCHEDULE.map((d, i) => {
            const active = i === dayIdx;
            return (
              <button
                key={d.num}
                role="tab"
                aria-selected={active}
                onClick={() => {
                  setDayIdx(i);
                  onSessionTap();
                }}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "14px 20px",
                  background: active ? t.ink : "transparent",
                  color: active ? t.bg : t.ink,
                  border: `1px solid ${t.ink}`,
                  fontFamily: t.font,
                  fontSize: 13,
                  fontWeight: 700,
                  letterSpacing: "0.05em",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  fontFeatureSettings: '"tnum" 1, "lnum" 1',
                  transition: reduce ? "none" : "background 180ms ease, color 180ms ease",
                }}
              >
                <span style={{ opacity: active ? 0.6 : 0.45, fontWeight: 500 }}>[{d.num}]</span>
                <span>{d.date}</span>
                <span style={{ opacity: active ? 0.7 : 0.5, fontWeight: 500 }}>· {d.title}</span>
              </button>
            );
          })}
        </div>

        {/* Track headers */}
        <div
          className="conf-track-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "80px repeat(4, 1fr)",
            borderTop: `1px solid ${t.ink}33`,
            borderLeft: `1px solid ${t.ink}33`,
          }}
        >
          <div
            className="conf-track-time-header"
            style={{
              padding: "16px 12px",
              borderRight: `1px solid ${t.ink}33`,
              borderBottom: `1px solid ${t.ink}33`,
              background: `${t.ink}05`,
              fontFamily: t.font,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.3em",
              color: `${t.ink}99`,
              fontFeatureSettings: '"tnum" 1, "lnum" 1',
            }}
          >
            TIME
          </div>
          {TRACKS.map((tr) => (
            <div
              key={tr.key}
              className="conf-track-col-header"
              style={{
                padding: "16px",
                borderRight: `1px solid ${t.ink}33`,
                borderBottom: `1px solid ${t.ink}33`,
                background: `${tr.color}08`,
                fontFamily: t.font,
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: tr.color,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <span style={{ width: 8, height: 8, background: tr.color }} />
              {tr.label}
            </div>
          ))}

          {/* Body */}
          {grid.map(({ time, row }) => (
            <ScheduleRow
              key={time}
              t={t}
              time={time}
              row={row}
              reduce={reduce}
              onTap={onSessionTap}
            />
          ))}
        </div>
      </div>

      {/* Mobile collapse */}
      <style>{`
        @media (max-width: 860px) {
          .conf-track-grid {
            grid-template-columns: 1fr !important;
            border-left: none !important;
          }
          .conf-track-col-header,
          .conf-track-time-header { display: none !important; }
          .conf-track-row { grid-template-columns: 1fr !important; }
          .conf-track-cell {
            border-right: none !important;
            border-left: 1px solid ${t.ink}33 !important;
          }
          .conf-track-time-cell {
            border-right: none !important;
            border-left: 1px solid ${t.ink}33 !important;
            background: ${t.ink}10 !important;
          }
        }
      `}</style>
    </section>
  );
}

function ScheduleRow({
  t,
  time,
  row,
  reduce,
  onTap,
}: {
  t: VariantProps["t"];
  time: string;
  row: Record<TrackKey, Session | null>;
  reduce: boolean;
  onTap: () => void;
}) {
  return (
    <div
      className="conf-track-row"
      style={{
        display: "contents",
      }}
    >
      <div
        className="conf-track-time-cell"
        style={{
          padding: "16px 12px",
          borderRight: `1px solid ${t.ink}33`,
          borderBottom: `1px solid ${t.ink}33`,
          fontFamily: t.font,
          fontSize: 14,
          fontWeight: 700,
          letterSpacing: "0.05em",
          color: t.ink,
          fontFeatureSettings: '"tnum" 1, "lnum" 1',
          background: `${t.ink}03`,
          display: "flex",
          alignItems: "flex-start",
        }}
      >
        {time}
      </div>
      {TRACKS.map((tr) => {
        const s = row[tr.key];
        return (
          <ScheduleCell
            key={tr.key}
            t={t}
            session={s}
            track={tr}
            reduce={reduce}
            onTap={onTap}
          />
        );
      })}
    </div>
  );
}

function ScheduleCell({
  t,
  session,
  track,
  reduce,
  onTap,
}: {
  t: VariantProps["t"];
  session: Session | null;
  track: TrackMeta;
  reduce: boolean;
  onTap: () => void;
}) {
  const [hover, setHover] = useState(false);
  const ink = t.ink;

  if (!session) {
    return (
      <div
        className="conf-track-cell"
        style={{
          padding: "16px",
          borderRight: `1px solid ${ink}33`,
          borderBottom: `1px solid ${ink}33`,
          minHeight: 88,
          background: `${ink}03`,
        }}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={onTap}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      className="conf-track-cell"
      style={{
        textAlign: "left",
        padding: "16px",
        borderRight: `1px solid ${ink}33`,
        borderBottom: `1px solid ${ink}33`,
        borderTop: "none",
        borderLeft: "none",
        minHeight: 88,
        background: hover ? `${track.color}10` : t.bg,
        cursor: "pointer",
        fontFamily: t.font,
        color: ink,
        display: "flex",
        flexDirection: "column",
        gap: 8,
        position: "relative",
        transition: reduce ? "none" : "background 160ms ease",
      }}
    >
      {/* track color rail on left */}
      <span
        aria-hidden
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 3,
          background: track.color,
          opacity: hover ? 1 : 0.7,
        }}
      />
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span
          className="conf-mobile-time"
          style={{
            display: "none",
            fontFamily: t.font,
            fontSize: 11,
            fontWeight: 700,
            color: track.color,
            fontFeatureSettings: '"tnum" 1, "lnum" 1',
            letterSpacing: "0.1em",
          }}
        >
          {session.time}
        </span>
        <TrackTag track={track.key} font={t.font} />
      </div>
      <div
        style={{
          fontSize: 14,
          fontWeight: 600,
          lineHeight: 1.4,
          letterSpacing: "-0.005em",
        }}
      >
        {session.title}
      </div>
      <div
        style={{
          marginTop: "auto",
          fontSize: 12,
          fontWeight: 500,
          color: `${ink}aa`,
          letterSpacing: "0.02em",
        }}
      >
        {session.speaker}
      </div>
      <style>{`
        @media (max-width: 860px) {
          .conf-mobile-time { display: inline-block !important; }
        }
      `}</style>
    </button>
  );
}

// ─── Speakers grid ──────────────────────────────────────────────────────────
function SpeakersGrid({
  t,
  reduce,
  onCardHover,
}: {
  t: VariantProps["t"];
  reduce: boolean;
  onCardHover: () => void;
}) {
  const tt = useT();
  return (
    <section
      style={{
        padding: "120px clamp(20px, 5vw, 48px)",
        borderTop: `1px solid ${t.ink}22`,
      }}
    >
      <div style={{ maxWidth: 1240, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 56 }}>
          <div
            style={{
              fontFamily: t.font,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.4em",
              color: t.accent,
              marginBottom: 12,
            }}
          >
            {tt("variant.c05.speakers.eyebrow") || "FEATURED SPEAKERS"}
          </div>
          <h2
            style={{
              fontFamily: t.font,
              fontSize: "clamp(36px, 5vw, 56px)",
              fontWeight: 800,
              letterSpacing: "-0.02em",
              margin: 0,
            }}
          >
            Speakers
          </h2>
          <p
            style={{
              maxWidth: 520,
              margin: "16px auto 0",
              fontFamily: t.font,
              fontSize: 15,
              lineHeight: 1.6,
              color: `${t.ink}cc`,
            }}
          >
            Tashkent, region va xalqaro maydondan 24 nafar speaker — bu yerda 6 nafari.
          </p>
        </div>
        <div
          className="conf-speakers"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 20,
          }}
        >
          {SPEAKERS.map((s) => (
            <SpeakerCard
              key={s.name}
              t={t}
              speaker={s}
              reduce={reduce}
              onHover={onCardHover}
            />
          ))}
        </div>
      </div>
      <style>{`
        @media (max-width: 960px) {
          .conf-speakers { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (max-width: 600px) {
          .conf-speakers { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}

function SpeakerCard({
  t,
  speaker,
  reduce,
  onHover,
}: {
  t: VariantProps["t"];
  speaker: Speaker;
  reduce: boolean;
  onHover: () => void;
}) {
  const [hover, setHover] = useState(false);
  const firedRef = useRef(false);

  const handleEnter = () => {
    setHover(true);
    if (!firedRef.current) {
      firedRef.current = true;
      onHover();
    }
  };
  const handleLeave = () => {
    setHover(false);
    // allow re-fire after pointer fully leaves
    firedRef.current = false;
  };

  const trackMeta = TRACKS.find((tr) => tr.key === speaker.track) ?? TRACKS[0];

  return (
    <div
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onFocus={handleEnter}
      onBlur={handleLeave}
      tabIndex={0}
      style={{
        position: "relative",
        background: t.bg,
        border: `1px solid ${t.ink}22`,
        padding: 16,
        cursor: "default",
        transform: hover ? "translateY(-2px)" : "translateY(0)",
        boxShadow: hover ? `0 6px 0 -2px ${trackMeta.color}55` : "none",
        transition: reduce ? "none" : "transform 180ms ease, box-shadow 180ms ease",
      }}
    >
      <div
        style={{
          aspectRatio: "1 / 1",
          background: `${t.ink}08`,
          marginBottom: 14,
          overflow: "hidden",
        }}
      >
        <GuestPhoto
          src={speaker.photo}
          style={{
            width: "100%",
            height: "100%",
            filter: hover ? "none" : "grayscale(0.6) contrast(1.05)",
            transition: reduce ? "none" : "filter 250ms ease",
          }}
        />
      </div>
      <div style={{ marginBottom: 8 }}>
        <TrackTag track={speaker.track} font={t.font} />
      </div>
      <div
        style={{
          fontFamily: t.font,
          fontSize: 17,
          fontWeight: 700,
          letterSpacing: "-0.01em",
          marginBottom: 4,
          color: t.ink,
        }}
      >
        {speaker.name}
      </div>
      <div
        style={{
          fontFamily: t.font,
          fontSize: 13,
          fontWeight: 500,
          color: `${t.ink}aa`,
          letterSpacing: "0.01em",
        }}
      >
        {speaker.role}
      </div>
    </div>
  );
}

// ─── Stat row ───────────────────────────────────────────────────────────────
function StatRow({ t }: { t: VariantProps["t"] }) {
  const stats: Array<[string, string]> = [
    ["3", "days"],
    ["24", "speakers"],
    ["8", "tracks"],
    ["1200", "attendees"],
  ];
  return (
    <div
      className="conf-stat-row"
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(4, 1fr)",
        marginTop: 56,
        border: `1px solid ${t.ink}33`,
      }}
    >
      {stats.map(([n, l], i) => (
        <div
          key={l}
          style={{
            padding: "20px 18px",
            borderRight: i < stats.length - 1 ? `1px solid ${t.ink}33` : "none",
            display: "flex",
            flexDirection: "column",
            gap: 4,
            background: i % 2 === 0 ? "transparent" : `${t.ink}03`,
          }}
        >
          <span
            style={{
              fontFamily: t.font,
              fontSize: "clamp(28px, 4vw, 40px)",
              fontWeight: 800,
              letterSpacing: "-0.03em",
              color: t.ink,
              fontFeatureSettings: '"tnum" 1, "lnum" 1',
              lineHeight: 1,
            }}
          >
            {n}
          </span>
          <span
            style={{
              fontFamily: t.font,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.3em",
              textTransform: "uppercase",
              color: `${t.ink}99`,
            }}
          >
            {l}
          </span>
        </div>
      ))}
      <style>{`
        @media (max-width: 720px) {
          .conf-stat-row { grid-template-columns: repeat(2, 1fr) !important; }
          .conf-stat-row > div:nth-child(1),
          .conf-stat-row > div:nth-child(2) {
            border-bottom: 1px solid ${t.ink}33;
          }
          .conf-stat-row > div:nth-child(2) { border-right: none !important; }
        }
      `}</style>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ConfTracksVariant — c05 TECH SUMMIT 2026
// ═══════════════════════════════════════════════════════════════════════════
export function ConfTracksVariant({ t, isDark, ctx }: VariantProps) {
  const tt = useT();
  const reduce = usePrefersReducedMotion();
  const { play } = useSound();

  const heroPhoto = t.photos?.[0];
  const addressLine =
    tt("variant.c05.map.address") || `Tashkent · International Conference Hall`;

  return (
    <div className="v-c05-variant" style={{ background: t.bg, color: t.ink, fontFamily: t.font }}>
      <style>{`
        .v-c05-variant button:focus-visible,
        .v-c05-variant a:focus-visible {
          outline: 2px solid ${t.ink};
          outline-offset: 2px;
        }
        @media (max-width: 768px) {
          .v-c05-hero { padding: 64px 18px 48px !important; min-height: auto !important; text-align: center !important; }
          .v-c05-hero h1 { font-size: clamp(36px, 12vw, 72px) !important; }
          .v-c05-hero-top-strip { justify-content: center !important; }
          .v-c05-variant button,
          .v-c05-variant a[role="button"] { min-height: 44px; }
          .conf-day-tabs button { min-height: 44px !important; padding: 12px 14px !important; font-size: 12px !important; }
        }
      `}</style>
      {/* ═══════════════════════════════════════════════════════════════════
          HERO
          ═══════════════════════════════════════════════════════════════════ */}
      <section
        className="v-c05-hero"
        style={{
          position: "relative",
          minHeight: "100vh",
          padding: "clamp(80px, 11vw, 140px) clamp(24px, 5vw, 60px) clamp(60px, 9vw, 100px)",
          display: "grid",
          alignContent: "center",
          background: t.bg,
          color: t.ink,
          overflow: "hidden",
        }}
      >
        {/* Hero photo — large, restrained, behind content */}
        {heroPhoto && (
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              backgroundImage: `url("${heroPhoto}")`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              filter: "grayscale(1) contrast(1.05) brightness(0.98)",
              opacity: 0.18,
              pointerEvents: "none",
            }}
          />
        )}
        {/* Paper grain overlay — radial vignette into cream */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            background: `radial-gradient(ellipse at 50% 30%, transparent 0%, ${t.bg} 75%)`,
            pointerEvents: "none",
          }}
        />

        <div style={{ position: "relative", maxWidth: 1240, margin: "0 auto", width: "100%" }}>
          {/* Top strip — eyebrow + locale */}
          <div
            className="v-c05-hero-top-strip"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 48,
              fontFamily: t.font,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.32em",
              color: t.ink,
              fontFeatureSettings: '"tnum" 1, "lnum" 1',
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            <span>[ TECH SUMMIT · 2026 ]</span>
            <span style={{ opacity: 0.55 }}>TASHKENT · UZ</span>
          </div>

          {/* Day stepper — signature */}
          <DayStepper reduce={reduce} ink={t.ink} font={t.font} />

          {/* Big headline */}
          <h1
            className="conf-hero-h1"
            style={{
              fontFamily: t.font,
              fontSize: "clamp(56px, 12vw, 168px)",
              fontWeight: 800,
              letterSpacing: "-0.04em",
              lineHeight: 0.94,
              margin: "32px 0 0",
              color: t.ink,
            }}
          >
            TECH SUMMIT
            <br />
            <span style={{ fontWeight: 500, letterSpacing: "-0.02em" }}>2026</span>
          </h1>

          {/* Subtitle */}
          <div
            style={{
              marginTop: 28,
              display: "flex",
              alignItems: "center",
              gap: 14,
              flexWrap: "wrap",
              fontFamily: t.font,
              fontSize: "clamp(15px, 1.4vw, 19px)",
              fontWeight: 500,
              color: `${t.ink}cc`,
              letterSpacing: "0.01em",
              fontFeatureSettings: '"tnum" 1, "lnum" 1',
            }}
          >
            <span>10 — 12 October 2026</span>
            <span style={{ opacity: 0.4 }}>·</span>
            <span>Tashkent</span>
            <span style={{ opacity: 0.4 }}>·</span>
            <span style={{ opacity: 0.7 }}>3 kun · 24 speaker · 8 yo'nalish</span>
          </div>

          {/* Stat row */}
          <StatRow t={t} />

          {/* CTA hint */}
          <div
            style={{
              marginTop: 40,
              fontFamily: t.font,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.3em",
              color: `${t.ink}88`,
            }}
          >
            ↓ SCROLL — AGENDA · SPEAKERS · RSVP
          </div>
        </div>

        <style>{`
          @media (max-width: 720px) {
            .conf-hero-h1 {
              font-size: clamp(40px, 14vw, 88px) !important;
              letter-spacing: -0.03em !important;
            }
          }
        `}</style>
      </section>

      {/* Countdown */}
      <CountdownSection t={t} {...ctx.countdown} isDark={isDark} />

      {/* Story — Why Tashkent, why now */}
      <StorySection
        t={t}
        eyebrow="Why Tashkent · why now"
        title="A new center of gravity"
        body="Tashkent is the fastest-growing tech hub in Central Asia — fluent in three languages, hungry to build, and increasingly the place where regional product decisions get made. TECH SUMMIT 2026 brings 1200 builders, founders, designers and operators into one room for three days. Bu yerda hammasi boshlanadi."
      />

      {/* Multi-track schedule (centerpiece) */}
      <TrackSchedule t={t} reduce={reduce} onSessionTap={() => play("tap")} />

      {/* Speakers grid */}
      <SpeakersGrid t={t} reduce={reduce} onCardHover={() => play("tap")} />

      {/* Map */}
      <MapSection t={t} isDark={isDark} addressLine={addressLine} />

      {/* RSVP — onClick wires through respond() inside the section, but we still
          want a tap accent on user interaction. We can't intercept the buttons
          inside RsvpSection without forking it; instead we play a tap on rsvp
          state-change (covered by ctx.rsvp.setRsvp wrapper below). */}
      <RsvpSection
        t={t}
        isDark={isDark}
        {...ctx.rsvp}
        setRsvp={(v) => {
          play("tap");
          ctx.rsvp.setRsvp(v);
        }}
        label="Reserve your seat"
        slug={ctx.slug}
      />

      {/* Share */}
      <ShareSection t={t} />

      {/* Footer */}
      <GuestFooter t={t} />
    </div>
  );
}
